import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';
import clsx from 'clsx';
import Button from '../../../../components/Button';
import { AuditLogItem } from '../../../../interfaces/auditLog.interface';

const COPIED_MS = 1200;
const BASE = 'auditLogs.list.pageTexts.detail';

interface RequestDetailsProps {
  item: Pick<AuditLogItem, 'ip_address' | 'batch_uuid' | 'user_agent'>;
}

/** The technical request details in a bordered section that can be collapsed (open by default), with a Copy for the request ID. */
const RequestDetails: React.FC<RequestDetailsProps> = ({ item }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleCopy = () => {
    // copy() reports whether the browser accepted the copy; "Copied" is only shown when it did
    if (!copy(item.batch_uuid)) return;
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  const row = (label: string, value: React.ReactNode) => (
    <React.Fragment key={label}>
      <dt className="col-sm-3 text-heading">{label}</dt>
      <dd className="col-sm-9 mb-2 text-break">{value}</dd>
    </React.Fragment>
  );

  return (
    <details className="border rounded" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="px-3 py-2 fw-bold text-heading cursor-pointer d-flex align-items-center">
        <i className="ri-information-line ri-18px me-2" aria-hidden="true" />
        {t(`${BASE}.sectionRequest`)}
        <i className={clsx('request-chevron ri-20px ms-auto', open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line')} aria-hidden="true" />
      </summary>
      <dl className="row g-0 px-3 pt-2 mb-0 border-top">
        {row(t(`${BASE}.ip`), item.ip_address)}
        {item.batch_uuid && row(
          t(`${BASE}.batch`),
          <>
            {item.batch_uuid}
            <Button
              className="btn btn-xs btn-text-secondary border ms-2"
              text={t(`${BASE}.${copied ? 'copied' : 'copy'}`)}
              onClick={handleCopy}
            />
          </>,
        )}
        {row(t(`${BASE}.userAgent`), item.user_agent)}
      </dl>
    </details>
  );
};

export default RequestDetails;
